import { LeverageParameters } from '@katanaperps/katana-perps-sdk';
import React from 'react';
import styled from 'styled-components';

import { ApiTriggerType } from 'api/types';
import TextColored from 'components/basic/text/TextColored';
import { IS_DEBUG_ENVIRONMENT } from 'config';
import { useIsMobile } from 'hooks/useIsMobile';
import {
  formatNumber,
  formatPercentage,
  formatUsdWithSign,
} from 'lib/display/formatting';
import { capitalizeFirstLetter } from 'lib/string';

import { estimateTPSLProfitOrLoss } from './tpSlUtils';

const Text = styled.div`
  margin: 8px 0;
  color: var(--gray4c);
  font-size: var(--small);
  font-style: normal;
  font-weight: normal;
  line-height: normal;
`;

const Highlighted = styled.span`
  color: var(--white);
`;

export default function Explanation({
  disabled,
  type,
  triggerType,
  price,
  tickSizeDecimals,
  entryPrice,
  quantity,
  marketLeverageParams,
  initialMarginFractionOverride,
}: {
  disabled: boolean;
  type: 'stopLoss' | 'takeProfit';
  triggerType: ApiTriggerType;
  price: string;
  tickSizeDecimals: number | undefined;
  entryPrice: string;
  quantity: string;
  marketLeverageParams: LeverageParameters | undefined;
  initialMarginFractionOverride: string | null | undefined;
}) {
  const isMobile = useIsMobile();
  if (disabled) {
    return null;
  }
  if (!marketLeverageParams) {
    console.log('Missing marketLeverageParams');
    return null;
  }
  if (initialMarginFractionOverride === undefined) {
    console.log('Missing initialMarginFractionOverride');
    return null;
  }

  const { pnL, pnLPercentage } = estimateTPSLProfitOrLoss({
    entryPrice,
    price,
    quantity,
    marketLeverageParams,
    initialMarginFractionOverride,
  });

  return (
    <Text>
      {IS_DEBUG_ENVIRONMENT && !isMobile && (
        <pre
          style={{
            opacity: 0.6,
            color: 'yellow',
            position: 'fixed',
            left: 10,
          }}
        >
          {JSON.stringify(
            {
              type,
              triggerType,
              entryPrice,
              price,
              quantity,
              tickSizeDecimals,
            },
            null,
            2
          )}
        </pre>
      )}
      When <Highlighted>{capitalizeFirstLetter(triggerType)}</Highlighted> Price
      reaches{' '}
      <Highlighted>
        {price ? formatNumber(price, { fixedDecimals: tickSizeDecimals }) : '-'}
      </Highlighted>
      , it will trigger a {type === 'stopLoss' ? 'Stop Loss' : 'Take Profit'}{' '}
      Market order to close this position. Estimated P&L is{' '}
      {pnL ? (
        <TextColored color={pnL >= 0 ? 'green' : 'red'}>
          {formatUsdWithSign(pnL)} ({formatPercentage(pnLPercentage)} %)
        </TextColored>
      ) : (
        '-'
      )}
      .
    </Text>
  );
}
